import { useState } from "react";
import {
  ExternalLink,
  Loader2,
  BadgeCheck,
  Trophy,
  Sparkles,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import ProviderLogo from "@/components/ProviderLogo";

// Brand colors per provider (subtle background gradient + accent)
const BRAND = {
  yellow: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-900", accent: "bg-amber-500" },
  red: { bg: "bg-red-50", border: "border-red-200", text: "text-red-900", accent: "bg-red-600" },
  lime: { bg: "bg-lime-50", border: "border-lime-200", text: "text-lime-900", accent: "bg-lime-600" },
  cyan: { bg: "bg-cyan-50", border: "border-cyan-200", text: "text-cyan-900", accent: "bg-cyan-600" },
  violet: { bg: "bg-violet-50", border: "border-violet-200", text: "text-violet-900", accent: "bg-violet-600" },
  emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-900", accent: "bg-emerald-600" },
  blue: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-900", accent: "bg-blue-600" },
  orange: { bg: "bg-orange-50", border: "border-orange-200", text: "text-orange-900", accent: "bg-orange-600" },
  indigo: { bg: "bg-indigo-50", border: "border-indigo-200", text: "text-indigo-900", accent: "bg-indigo-600" },
};

export default function ProviderGrid({ top = [], all = [], onProviderClick, possibleSavings = 0 }) {
  const [clickingId, setClickingId] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const topIds = new Set(top.map((p) => p.id));
  const rest = all.filter((p) => !topIds.has(p.id));

  const handleClick = async (providerId) => {
    setClickingId(providerId);
    try {
      await onProviderClick?.(providerId);
    } finally {
      setTimeout(() => setClickingId(null), 800);
    }
  };

  return (
    <div data-testid="provider-grid">
      {/* TOP 3 — featured cards */}
      {top.length > 0 && (
        <div className="grid lg:grid-cols-3 gap-4 lg:gap-5 mb-8">
          {top.map((p, idx) => (
            <FeaturedCard
              key={p.id}
              provider={p}
              rank={idx + 1}
              loading={clickingId === p.id}
              onClick={() => handleClick(p.id)}
              possibleSavings={idx === 0 ? possibleSavings : 0}
            />
          ))}
        </div>
      )}

      {/* All others — compact list */}
      {rest.length > 0 && (
        <div data-testid="provider-grid-other">
          <button
            type="button"
            onClick={() => setShowAll(!showAll)}
            className="flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-blue-700 transition-colors mb-4"
            data-testid="provider-grid-toggle"
          >
            {showAll ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            {showAll ? "Verberg" : `Bekijk alle ${rest.length} andere aanbieders`}
          </button>
          {showAll && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {rest.map((p) => (
                <CompactCard
                  key={p.id}
                  provider={p}
                  loading={clickingId === p.id}
                  onClick={() => handleClick(p.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <p className="mt-6 text-[11px] text-slate-500 max-w-2xl">
        EnergieKontract ontvangt een vergoeding wanneer je via onze link overstapt — dit beïnvloedt de prijs die jij betaalt niet
        en het advies blijft onafhankelijk en gebaseerd op jouw situatie.
      </p>
    </div>
  );
}

function FeaturedCard({ provider, rank, loading, onClick, possibleSavings = 0 }) {
  const brand = BRAND[provider.logo_color] || BRAND.blue;
  const isFirst = rank === 1 || provider.is_best_match;
  const fmt = (v) => new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v);
  return (
    <div
      className={`relative overflow-hidden bg-white rounded-2xl border-2 transition-all hover:-translate-y-0.5 ${
        isFirst ? "border-blue-700 shadow-[0_16px_48px_-16px_rgba(29,78,216,0.35)]" : "border-slate-200 shadow-[0_8px_24px_-12px_rgba(15,23,42,0.1)]"
      }`}
      data-testid={`provider-featured-${provider.id}`}
    >
      {/* Top accent bar */}
      <div className={`h-1.5 w-full ${brand.accent}`} />

      <div className="p-5 sm:p-6">
        {/* Rank badge */}
        <div className="flex items-start justify-between mb-4">
          <span
            className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${
              isFirst
                ? "bg-blue-700 text-white"
                : rank === 2
                ? "bg-slate-100 text-slate-700"
                : "bg-amber-100 text-amber-900"
            }`}
            data-testid={`provider-rank-${provider.id}`}
          >
            {isFirst ? (
              <>
                <Trophy className="h-3 w-3" />
                Beste match
              </>
            ) : (
              <>#{rank}</>
            )}
          </span>
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700">
            <BadgeCheck className="h-3 w-3" />
            ACM-erkend
          </span>
        </div>

        {/* Logo + name */}
        <div className="flex items-center gap-3 mb-3">
          <ProviderLogo providerId={provider.id} name={provider.name} size={48} />
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-lg font-bold text-slate-900 truncate">{provider.name}</h3>
            <p className="text-xs text-slate-500 truncate">{provider.tagline}</p>
          </div>
        </div>

        {/* Savings highlight (only on #1) */}
        {isFirst && possibleSavings > 0 && (
          <div className="mb-4 rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2.5">
            <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-800">Geschatte besparing</div>
            <div className="font-heading text-xl font-bold text-emerald-700">{fmt(possibleSavings)}<span className="text-xs font-normal text-emerald-700/80">/jaar</span></div>
          </div>
        )}

        {/* Match reasons */}
        {provider.match_reasons && provider.match_reasons.length > 0 && (
          <ul className="space-y-1.5 mb-4" data-testid={`provider-reasons-${provider.id}`}>
            {provider.match_reasons.map((r, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                <Sparkles className="h-3 w-3 text-blue-700 flex-shrink-0 mt-0.5" />
                <span className="leading-snug">{r}</span>
              </li>
            ))}
          </ul>
        )}

        {/* Badges */}
        {(!provider.match_reasons || provider.match_reasons.length === 0) && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {provider.badges.slice(0, 3).map((b, i) => (
              <span key={i} className="text-[10px] text-slate-600 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-full">
                {b}
              </span>
            ))}
          </div>
        )}

        {/* CTA */}
        <button
          onClick={onClick}
          disabled={loading}
          className={`w-full inline-flex items-center justify-center gap-2 font-semibold rounded-lg px-4 py-3 text-sm transition-all disabled:opacity-50 ${
            isFirst
              ? "bg-blue-700 hover:bg-blue-800 text-white shadow-sm"
              : "bg-slate-900 hover:bg-slate-800 text-white"
          }`}
          data-testid={`provider-cta-${provider.id}`}
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Doorsturen...
            </>
          ) : (
            <>
              Stap nu over
              <ExternalLink className="h-3.5 w-3.5" />
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function CompactCard({ provider, loading, onClick }) {
  const brand = BRAND[provider.logo_color] || BRAND.blue;
  return (
    <div
      className="bg-white border border-slate-200 rounded-xl p-4 hover:border-blue-200 hover:shadow-md transition-all flex flex-col"
      data-testid={`provider-compact-${provider.id}`}
    >
      <div className="flex items-center gap-3 mb-2">
        <ProviderLogo providerId={provider.id} name={provider.name} size={36} />
        <div className="flex-1 min-w-0">
          <h4 className="font-heading text-sm font-semibold text-slate-900 truncate">{provider.name}</h4>
          <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700">
            <BadgeCheck className="h-2.5 w-2.5" />
            ACM-erkend
          </span>
        </div>
      </div>
      <p className="text-[11px] text-slate-600 leading-snug mb-3 flex-1">{provider.tagline}</p>
      <button
        onClick={onClick}
        disabled={loading}
        className="inline-flex items-center justify-center gap-1.5 bg-white border border-slate-200 hover:border-blue-300 hover:bg-blue-50 text-slate-700 hover:text-blue-700 font-medium rounded-lg px-3 py-2 text-xs transition-all disabled:opacity-50"
        data-testid={`provider-cta-${provider.id}`}
      >
        {loading ? (
          <>
            <Loader2 className="h-3 w-3 animate-spin" />
            Doorsturen...
          </>
        ) : (
          <>
            Stap nu over
            <ExternalLink className="h-3 w-3" />
          </>
        )}
      </button>
    </div>
  );
}
